import { useMutation, useQueryClient } from 'libs/query'
import { useTranslation } from 'libs/i18n'
import { useAxiosPrivate, useToast } from 'shared/hooks'
import { apiRoutes } from 'config/apiRoutes'
import { queryKeys } from 'config/queryKeys'
import axios from 'libs/fetch'

export type RowError = { row: number | null; column: string | null; message: string }

type UseImportProductsMutationProps = {
	bookingId: string
	onSuccess: () => void
	onRowErrors: (rowErrors: RowError[]) => void
}

export const useImportProductsMutation = ({
	bookingId,
	onSuccess,
	onRowErrors
}: UseImportProductsMutationProps) => {
	const { t } = useTranslation()
	const axiosPrivate = useAxiosPrivate()
	const queryClient = useQueryClient()

	const { mutate: importProductsMutation, isPending: isImportProductsLoading } = useMutation({
		mutationFn: (file: File) => {
			const formData = new FormData()
			formData.append('csv_file', file)
			return axiosPrivate.post(apiRoutes.bookingProductImport(bookingId), formData, {
				headers: { 'Content-Type': 'multipart/form-data' }
			})
		},
		onSuccess: (res) => {
			queryClient.invalidateQueries({ queryKey: queryKeys.products.all })
			useToast({
				type: 'success',
				message: t('{{count}} products imported', { count: res.data.length })
			})()
			onSuccess()
		},
		onError: (error) => {
			if (axios.isAxiosError(error) && error.response?.data?.row_errors) {
				onRowErrors(error.response.data.row_errors)
				return
			}
			if (axios.isAxiosError(error) && error.response?.data?.detail) {
				useToast({ type: 'error', message: t(error.response.data.detail) })()
				return
			}
			useToast({ type: 'error', message: t('Something went wrong') })()
		}
	})

	return { importProductsMutation, isImportProductsLoading }
}
